import type { LocaleData } from "i18n-iso-countries";
import { APP_LANGUAGES, resolveAppLanguage, type AppLanguage } from "../i18n";

export type CountryLocale = AppLanguage;

const SUPPORTED_LOCALES: CountryLocale[] = [...APP_LANGUAGES];

type CountriesApi = {
  registerLocale: (data: LocaleData) => void;
  getName: (code: string, lang: string) => string | undefined;
  getNames: (lang: string, options?: { select?: string }) => Record<string, string>;
  getAlpha2Codes: () => Record<string, string>;
  isValid: (code: string) => boolean;
};

let countriesApi: CountriesApi | null = null;
let loadPromise: Promise<void> | null = null;

async function loadCountriesModule(): Promise<CountriesApi> {
  const countries = (await import("i18n-iso-countries")).default as CountriesApi;
  // Explicit imports keep CRA/webpack bundling reliable for all app language packs.
  const packs = await Promise.all([
    import("i18n-iso-countries/langs/ar.json"),
    import("i18n-iso-countries/langs/bg.json"),
    import("i18n-iso-countries/langs/cs.json"),
    import("i18n-iso-countries/langs/da.json"),
    import("i18n-iso-countries/langs/de.json"),
    import("i18n-iso-countries/langs/el.json"),
    import("i18n-iso-countries/langs/en.json"),
    import("i18n-iso-countries/langs/es.json"),
    import("i18n-iso-countries/langs/et.json"),
    import("i18n-iso-countries/langs/fi.json"),
    import("i18n-iso-countries/langs/fr.json"),
    import("i18n-iso-countries/langs/ga.json"),
    import("i18n-iso-countries/langs/hr.json"),
    import("i18n-iso-countries/langs/hu.json"),
    import("i18n-iso-countries/langs/it.json"),
    import("i18n-iso-countries/langs/lt.json"),
    import("i18n-iso-countries/langs/lv.json"),
    import("i18n-iso-countries/langs/mt.json"),
    import("i18n-iso-countries/langs/nl.json"),
    import("i18n-iso-countries/langs/pl.json"),
    import("i18n-iso-countries/langs/pt.json"),
    import("i18n-iso-countries/langs/ro.json"),
    import("i18n-iso-countries/langs/ru.json"),
    import("i18n-iso-countries/langs/sk.json"),
    import("i18n-iso-countries/langs/sl.json"),
    import("i18n-iso-countries/langs/sv.json"),
    import("i18n-iso-countries/langs/uk.json"),
    import("i18n-iso-countries/langs/zh.json"),
  ]);
  for (const mod of packs) {
    countries.registerLocale((mod.default ?? mod) as LocaleData);
  }
  return countries;
}

/** Lazy-loads i18n-iso-countries (code-split). Safe to call multiple times. */
export function ensureCountriesLoaded(): Promise<void> {
  if (countriesApi) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = loadCountriesModule()
      .then((api) => {
        countriesApi = api;
      })
      .catch((err) => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
}

function resolveLocale(lng?: string): CountryLocale {
  return resolveAppLanguage(lng);
}

export function countryLabel(code: string | null | undefined, lng?: string): string {
  if (!code) return "";
  const normalized = code.trim().toUpperCase();
  if (!normalized) return "";
  if (!countriesApi) return normalized;
  const locale = resolveLocale(lng);
  const name = countriesApi.getName(normalized, locale);
  return name || normalized;
}

export type CountryOption = { label: string; value: string };

export function countryOptions(lng?: string): CountryOption[] {
  if (!countriesApi) return [];
  const locale = resolveLocale(lng);
  const names = countriesApi.getNames(locale, { select: "official" });
  return Object.entries(names)
    .map(([value, label]) => ({ value: value.toUpperCase(), label: String(label) }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

export function countryAlpha2Codes(): string[] {
  if (!countriesApi) return [];
  return Object.keys(countriesApi.getAlpha2Codes()).map((c) => c.toUpperCase());
}

export function isValidCountryCode(code: string): boolean {
  if (!countriesApi) return /^[A-Z]{2}$/.test(code.trim().toUpperCase());
  return countriesApi.isValid(code.trim().toUpperCase());
}

/** Alpha-2 from form value (code or localized country name). */
export function resolveFormCountryCode(value: unknown): string {
  const s = String(value ?? "").trim();
  if (!s) return "";
  const upper = s.toUpperCase();
  if (isValidCountryCode(upper)) return upper;
  if (!countriesApi) return upper;
  const needle = s.toLowerCase();
  for (const locale of SUPPORTED_LOCALES) {
    const names = countriesApi.getNames(locale, { select: "official" });
    for (const [code, name] of Object.entries(names)) {
      if (String(name).toLowerCase() === needle) return code.toUpperCase();
    }
  }
  return upper;
}

export function filterCountryOption(
  input: string,
  option?: { label?: string; value?: string }
): boolean {
  const q = input.trim().toLowerCase();
  if (!q) return true;
  const label = String(option?.label ?? "").toLowerCase();
  const value = String(option?.value ?? "").toLowerCase();
  return label.includes(q) || value.includes(q);
}
